import type { Icon } from "@phosphor-icons/react";
import { CheckCircleIcon } from "@phosphor-icons/react/dist/csr/CheckCircle";
import { CircleDashedIcon } from "@phosphor-icons/react/dist/csr/CircleDashed";
import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import type { ColumnDef } from "@tanstack/react-table";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { textVariants } from "@volca/ui/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { cn } from "@volca/ui/lib/utils";
import { dayjs } from "@/core/utils/date";
import {
  ApprovalHistory,
  drawableApprovalHistory,
} from "@/payroll/pay-breakdown/payroll-statement-notifications/approval-history";
import type { PayrollStatementNotificationRecipient } from "@/payroll/pay-breakdown/payroll-statement-notifications/types";
import { canApprovePack } from "@/payroll/pay-breakdown/payroll-statement-notifications/utils";

/**
 * The icon-and-label the cell shows, built from PHRASING content.
 *
 * phrasing content only, and every one of these faces now sits inside the tooltip's
 * trigger button — so flow content there is invalid HTML. React does not warn about it
 * (`validateDOMNesting` checks parents like `<p>` and `<li>`, not button content
 * models), which is exactly why it went unnoticed while this column had one such
 * trigger; it now has four.
 *
 */
const Face = ({
  icon: Icon,
  iconWeight,
  iconClassName,
  className,
  tone,
  state,
  children,
}: {
  icon: Icon;
  iconWeight?: "fill";
  iconClassName?: string;
  className?: string;
  tone?: "muted";
  state?: string;
  children: React.ReactNode;
}) => (
  <span className="inline-flex items-center gap-1">
    <Icon
      aria-hidden
      weight={iconWeight}
      className={cn("size-4 shrink-0", iconClassName)}
    />
    {state && <span className="sr-only">{state}</span>}
    <span className={cn(textVariants({ variant: "caption", tone }), className)}>
      {children}
    </span>
  </span>
);

/**
 * Whether this technician has approved their own pay.
 *
 * Five states. An approval of figures that have moved since is called out rather than
 * shown as a tick, because the manager is about to settle the period on the strength
 * of it — and the technician agreed to a number that is no longer on the pack. And a
 * technician who cannot approve at all gets a dash rather than "Not yet", which would
 * describe them as holding something up.
 *
 * "Moved" is compared by content digest, not by run: an edit to one cell under the
 * same run counts, which is the case this is mostly for. So the copy here says the
 * figures changed rather than naming a rerun, which would be wrong more often than
 * right.
 *
 * The fifth is an approval the API could not compare at all (`current: null`), and it
 * is neither of the other two: a green tick would claim the figures still stand, and
 * the warning would claim they moved. It gets the date under a plain tick, with the
 * hover saying why there is no warning on it. Drawing it as the warning is what put
 * "Pay changed" down a whole column of technicians whose pay had not changed, which
 * is the alert nobody could explain from the edit history (ENG-2511).
 *
 * Each of those states is the LATEST act and nothing more, which is as far as a cell
 * this size can go — so the acts behind it are on hover. That is what turns "Not yet"
 * into "they approved on Monday and withdrew it on Tuesday", and "Pay changed" into
 * when it changed relative to the approval it overtook.
 */
export const ApprovalCell = ({
  recipient,
}: {
  recipient: PayrollStatementNotificationRecipient;
}) => {
  const approval = recipient.pack_approval;
  // Filtered to what this build can draw, so an act from a newer API neither crashes
  // the row nor counts towards a hover that would come up empty.
  const history = drawableApprovalHistory(recipient.approval_history ?? []);

  // Nothing is being waited on from somebody who cannot approve — no pack was sent and
  // none can be. "Not yet" would read as an outstanding action that will never come.
  if (!approval && !canApprovePack(recipient)) {
    return (
      <Text variant="caption" tone="muted" aria-label="Not applicable">
        -
      </Text>
    );
  }

  const when = approval ? dayjs(approval.approved_at).format("MMM D") : null;

  const face = !approval ? (
    <Face icon={CircleDashedIcon} iconClassName="text-subtle" tone="muted">
      Not yet
    </Face>
  ) : approval.current === true ? (
    <Face
      icon={CheckCircleIcon}
      iconWeight="fill"
      iconClassName="text-success"
      state="Approved"
    >
      {when}
    </Face>
  ) : approval.current === false ? (
    <Face
      icon={WarningIcon}
      iconClassName="text-warning"
      className="text-warning"
    >
      Pay changed
    </Face>
  ) : (
    // They approved; whether the pack still says what they agreed to is unknown. The
    // date without the green is the honest half of the tick.
    <Face
      icon={CheckCircleIcon}
      iconClassName="text-subtle"
      tone="muted"
      state="Approved, not checked against these figures"
    >
      {when}
    </Face>
  );

  // Why the tick is grey, said ALONGSIDE the history rather than in place of it.
  //
  // The other two states can leave this to the history: a `false` comparison puts a
  // `pay_changed` act in it, which is the explanation. A `null` one puts nothing
  // there — the API cannot report a change it could not find — so the history reads
  // "Approved Sep 8" under a tick deliberately not green, with nothing to account for
  // the difference. Written as a `fallback` it would never render at all, because a
  // standing approval always has at least one act and the history wins.
  const unverified = !!approval && approval.current === null;

  // What the tooltip says when the API has no history for this technician — an older
  // approval, or a web deploy that landed ahead of the one that serves it. Losing the
  // explanation entirely would be worse than repeating the cell.
  const fallback =
    approval && approval.current === false
      ? `Approved ${when}, before these figures changed.`
      : unverified
        ? `Approved ${when}.`
        : null;

  if (history.length === 0 && !fallback) return face;

  return (
    <Tooltip>
      {/* A real button, not a focusable div — the history is the only place some of
          this is said, so it has to be reachable by keyboard and announced as
          interactive. Its contents are phrasing content so the nesting is valid; see
          Face above. */}
      <TooltipTrigger className="cursor-default text-left">
        {face}
      </TooltipTrigger>
      <TooltipContent className="max-w-md">
        <Stack align="start" gap={1}>
          {history.length > 0 ? (
            <ApprovalHistory entries={history} />
          ) : (
            fallback
          )}
          {unverified && (
            /* `primaryInverse`, like everything else in here: the tooltip surface is
               dark, and `secondary` is a light-surface token that renders this all but
               invisible on it. */
            <Text variant="caption" tone="inverse" className="opacity-70">
              We cannot tell whether these figures have changed since.
            </Text>
          )}
        </Stack>
      </TooltipContent>
    </Tooltip>
  );
};

export const ApprovalCol = (): ColumnDef<
  PayrollStatementNotificationRecipient,
  unknown
> => ({
  id: "pack_approval",
  enableSorting: false,
  header: () => <span>Tech approved</span>,
  cell: ({ row }) => <ApprovalCell recipient={row.original} />,
});
